import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { ChatbotUsageSummary } from '../../lib/types';
import { chatbotFade } from './chatbotShared';

export default function ChatbotUsageTab() {
  const [data, setData] = useState<ChatbotUsageSummary | null>(null);
  const [error, setError] = useState('');
  const maxDay = useMemo(() => Math.max(1, ...(data?.daily.map((day) => day.messages) ?? [1])), [data]);

  useEffect(() => {
    api.chatbot.usage().then(setData).catch(() => setError('No se pudieron cargar las métricas de uso.'));
  }, []);

  if (error) return <p className="text-sm text-ui-danger">{error}</p>;
  if (!data) return <div className="flex items-center gap-2 text-sm text-ui-text-subtle"><Loader2 size={15} className="animate-spin" /> Cargando…</div>;

  const cards = [
    { label: 'Respuestas (30 días)', value: data.totals.messages.toLocaleString('es-MX') },
    { label: 'Conversaciones', value: data.conversationsTotal.toLocaleString('es-MX') },
    { label: 'Costo estimado', value: `$${data.totals.cost.toFixed(2)} USD` },
    { label: 'Conversaciones marcadas', value: data.flaggedCount.toLocaleString('es-MX') },
  ];

  return (
    <motion.div {...chatbotFade} className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="rounded-lg border border-ui-border bg-ui-surface p-4">
            <p className="text-sm text-ui-text-subtle">{card.label}</p>
            <p className="text-h-sm font-semibold text-ui-text mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-ui-border bg-ui-surface p-4">
        <p className="text-sm font-medium text-ui-text mb-3">Respuestas por día</p>
        {data.daily.length === 0 ? (
          <p className="text-sm text-ui-text-subtle">Aún no hay actividad registrada.</p>
        ) : (
          <div className="space-y-1.5">
            {data.daily.map((day) => (
              <div key={day.day} className="flex items-center gap-3">
                <span className="w-20 text-sm text-ui-text-subtle tabular-nums">{day.day.slice(5)}</span>
                <div className="flex-1 h-4 bg-ui-bg rounded overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(day.messages / maxDay) * 100}%` }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="h-full bg-ui-accent/70"
                  />
                </div>
                <span className="w-10 text-right text-sm text-ui-text-muted tabular-nums">{day.messages}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-sm text-ui-text-subtle">
        El costo es una estimación para darte visibilidad; puede variar según el modelo que uses.
      </p>
    </motion.div>
  );
}
